/*
Purpose: Guided configuration UI for setting up an analysis (Tier 2 Configured Scan).
Audience: Any user wanting control over targets, model, depth, and instructions.
What it does:
- Presents targets (vehicles include plates), model choices, and template presets
- Estimates cost and validates configuration before launch
- Emits callbacks to start analysis via The Operator backend
Interplay:
- Reads centralized AI templates/models (frontend mirror of backend config)
- Used inside EvidenceModal to kick off analyses
- Works with AdvancedAnalysisModal for power-user scenarios
*/
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FaRobot, FaFileAlt, FaMicrophone, FaCar, FaUsers, FaPlay, FaCheckCircle, FaCog, FaChevronLeft, FaChevronRight, FaCloudRain, FaVideo } from 'react-icons/fa';
import { ANALYSIS_TEMPLATES, getAvailableModels, getScanTierConfig, mapModelToPreferredService } from '../../config/aiModelsConfig';
import log from '../../utils/logger';
import { useNotifications } from '../notifications/NotificationsProvider';
import AnalysisConfirmationModal from './AnalysisConfirmationModal';
import AdvancedAnalysisModal from './AdvancedAnalysisModal';
import '../../styles/AnalysisConfiguration.css';

const AnalysisConfigurationMenu = ({ evidence, casefileId, onAnalysisStarted, currentUser }) => {
  const { notify } = useNotifications();
  const [selectedTargets, setSelectedTargets] = useState(['vehicles']);
  
  // Per-target tier selection (Standard, Professional, Forensic)
  const [targetTiers, setTargetTiers] = useState({
    vehicles: 'standard',        // Default to Standard
    environment: 'standard',     // Default to Standard
    videographer: 'standard',    // Default to Standard
    people: 'standard',
    text: 'standard',
    audio: 'standard'
  });

  const [userPreferences] = useState({
    analysisDepth: 'Standard'
  });

  const [costEstimation, setCostEstimation] = useState({
    estimatedCost: 0,
    breakdown: {},
    confidence: 0.9,
    valid: true,
    reason: null
  });
  const [isLaunching, setIsLaunching] = useState(false);
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [currentScrollIndex, setCurrentScrollIndex] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  
  // Debug: Track modal state changes
  useEffect(() => {
    console.log('🎭 showConfirmationModal changed to:', showConfirmationModal);
  }, [showConfirmationModal]);
  const [pendingAnalysisRequest, setPendingAnalysisRequest] = useState(null);
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash-lite'); // Default to most cost-effective
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedTier, setSelectedTier] = useState(() => mapModelToPreferredService('gemini-2.5-flash-lite'));
  const [precisionMode, setPrecisionMode] = useState(false); // NEW: Precision Analysis toggle
  const templateScrollRef = useRef(null);

  // Analysis Target Options (UI configuration using centralized targets from templates)
  const analysisTargets = useMemo(() => [
    {
      id: 'vehicles',
      name: 'Vehicles',
      icon: FaCar,
      description: 'Detect and track vehicles; make/model ID; license plates; marks',
      cost: 0.0015, // per minute
      color: '#007bff'
    },
    {
      id: 'environment',
      name: 'Environment',
      icon: FaCloudRain,
      description: 'Weather, lighting, location characteristics',
      cost: 0.0008,
      color: '#10b981'
    },
    {
      id: 'videographer',
      name: 'Videographer',
      icon: FaVideo,
      description: 'Camera movements, recording perspective, operator intent',
      cost: 0.0005,
      color: '#f59e0b'
    },
    {
      id: 'people',
      name: 'People',
      icon: FaUsers,
      description: 'Detect people, facial analysis, behavior tracking (Coming in Phase 2)',
      cost: 0.002,
      color: '#ffc107',
      disabled: true,
      comingSoon: true
    },
    {
      id: 'text',
      name: 'Text & OCR',
      icon: FaFileAlt,
      description: 'Extract text from images and documents (Coming in Phase 2)',
      cost: 0.001,
      color: '#6610f2',
      disabled: true,
      comingSoon: true
    },
    {
      id: 'audio',
      name: 'Audio Analysis',
      icon: FaMicrophone,
      description: 'Speech transcription and audio analysis (Coming in Phase 2)',
      cost: 0.006,
      color: '#e83e8c',
      disabled: true,
      comingSoon: true
    }
  ], []);



  // Analysis Templates (using centralized configuration)
  const analysisTemplates = Object.values(ANALYSIS_TEMPLATES).map(template => ({
    id: template.id,
    name: template.name,
    description: template.description,
    targets: template.targets,
    estimatedCost: Array.isArray(template.estimated_cost_range) 
      ? template.estimated_cost_range[0] 
      : template.estimated_cost_range || 4.75
  }));

  // Calculate real-time cost estimation
  useEffect(() => {
      const calculateCost = () => {
      let totalCost = 0;
      const duration = evidence?.metadata?.duration || 1; // minutes
      const breakdown = {};

      selectedTargets.forEach(targetId => {
        const target = analysisTargets.find(t => t.id === targetId);
        if (target) {
          const targetCost = target.cost * duration;
          totalCost += targetCost;
          breakdown[targetId] = targetCost;
        }
      });

      // Add base model cost (tier-aware for Tier 2)
      const preferredService = mapModelToPreferredService(selectedModel);
      const baseModelCost = preferredService === 'google-vertex-vision-pro' ? 0.25
        : preferredService === 'google-vertex-vision-flash' ? 0.15
        : 0.10;
      totalCost += baseModelCost;
      breakdown.model_cost = baseModelCost;

      setCostEstimation({
        estimatedCost: Math.round(totalCost * 1000) / 1000,
        breakdown,
        confidence: 0.9,
        duration
      });
    };

    calculateCost();
  }, [selectedTargets, evidence, analysisTargets, selectedModel]);

  // Handle target selection
  const toggleTarget = (targetId) => {
    setSelectedTargets(prev => {
      const next = prev.includes(targetId)
        ? prev.filter(id => id !== targetId)
        : [...prev, targetId];
      return next;
    });
  };



  // Apply template
  const applyTemplate = (template) => {
    if (selectedTemplate === template.id) {
      // Deselect if already selected
      setSelectedTemplate(null);
      setSelectedTargets(['vehicles']); // Reset to default
    } else {
      // Select new template
      setSelectedTemplate(template.id);
      setSelectedTargets(template.targets);
    }
  };

  // Scroll template functions
  const scrollLeft = () => {
    setCurrentScrollIndex(prev => 
      prev === 0 ? analysisTemplates.length - 1 : prev - 1
    );
  };

  const scrollRight = () => {
    setCurrentScrollIndex(prev => 
      prev === analysisTemplates.length - 1 ? 0 : prev + 1
    );
  };

  // Load available AI models
  useEffect(() => {
    const models = getAvailableModels();
    setAvailableModels(models);
    console.log('✅ Available AI models loaded:', models);
  }, []);

  // Initialize The Operator service registry
  useEffect(() => {
    const initializeRegistry = async () => {
      try {
        const functions = getFunctions();
        const initRegistry = httpsCallable(functions, 'initializeServiceRegistry');
        await initRegistry();
        log.info('✅ The Operator service registry initialized');
      } catch (error) {
        log.warn('Service registry may already be initialized:', error?.message || error);
      }
    };
    initializeRegistry();
  }, []);

  const updateCostEstimation = useCallback(async () => {
    try {
      const functions = getFunctions();
      const estimateCost = httpsCallable(functions, 'estimateAnalysisCost');
      
      const evidenceReferences = [{
        casefileId: casefileId,
        evidenceId: evidence.id,
        filePath: evidence.filePath,
      }];

      const analysisRequest = {
        scanTier: 'configured_scan',
        targets: selectedTargets,
        depth: userPreferences.analysisDepth || 'Standard',
        selectedModel: selectedModel,
        preferredService: mapModelToPreferredService(selectedModel)
      };

      const response = await estimateCost({evidenceReferences, analysisRequest});
      setCostEstimation({
        estimatedCost: response.data.estimatedCost,
        breakdown: { services: response.data.selectedServices },
        confidence: 0.9,
        valid: response.data.valid !== false,
        reason: response.data.reason || null
      });
      
    } catch (error) {
      console.error('Cost estimation failed:', error);
      setCostEstimation(prev => ({
        ...prev,
        estimatedCost: 0.25, // Fallback estimate
        breakdown: {},
        confidence: 0.5,
        valid: true,
        reason: null
      }));
    }
  }, [casefileId, evidence, selectedTargets, userPreferences.analysisDepth, selectedModel]);

  // Update cost estimation when targets change
  useEffect(() => {
    if (selectedTargets.length > 0 && evidence) {
      updateCostEstimation();
    }
  }, [selectedTargets, evidence, updateCostEstimation]);

  // Show confirmation modal before launching analysis
  const launchAnalysis = () => {
    console.log('🚀 Launch button clicked!', {
      selectedTargets,
      selectedModel,
      isLaunching,
      costEstimation,
      precisionMode  // DEBUG: Log precision mode state
    });
    
    // Get configured_scan tier configuration
    const configuredScanConfig = getScanTierConfig('configured_scan');
    
    // Create analysis request compatible with The Operator using scan tier configuration
    console.log('🎯 DEBUG: precisionMode state value:', precisionMode); // DEBUG
    const analysisRequest = {
      scanTier: 'configured_scan', // NEW: Specify scan tier
      targets: selectedTargets,
      targetTiers: targetTiers, // NEW: Per-target tier selection (standard/professional/forensic)
      depth: userPreferences.analysisDepth || configuredScanConfig.analysis_depth,
      selectedModel: selectedModel, // Include selected AI model
      preferredService: mapModelToPreferredService(selectedModel), // Centralized mapping
      maxCostLimit: configuredScanConfig.max_cost_limit, // NEW: Cost ceiling
      precisionMode: precisionMode, // NEW: Precision Analysis mode toggle
      customInstructions: `Professional analysis requested by ${currentUser?.displayName || 'user'}. 
        ${userPreferences.detailedOutput ? 'Include detailed metadata and timestamps. ' : ''}
        ${userPreferences.autoCorrelate ? 'Cross-reference with existing case evidence. ' : ''}
        ${userPreferences.exportForCourt ? 'Generate court-ready documentation with chain of custody. ' : ''}
        Confidence threshold: ${userPreferences.confidenceThreshold * 100}%.`
    };

    // DEBUG: Log the analysis request to verify user selections
    log.debug('🔍 AnalysisConfigurationMenu - Analysis Request:', {
      targets: selectedTargets,
      selectedModel: selectedModel,
      depth: userPreferences.analysisDepth || 'Standard',
      selectedTemplate: selectedTemplate
    });

    // Store the request and show confirmation modal
    console.log('📋 Setting pending analysis request:', analysisRequest);
    setPendingAnalysisRequest(analysisRequest);
    console.log('🎭 Opening confirmation modal...');
    setShowConfirmationModal(true);
  };

  // Actually launch the analysis after confirmation
  const confirmAndLaunchAnalysis = async () => {
    try {
      setIsLaunching(true);
      const functions = getFunctions();
      const createOrder = httpsCallable(functions, 'createAnalysisOrder');
      
      const orderConfig = {
        scanTier: 'configured_scan', // NEW: Specify scan tier
        casefileId: casefileId,
        evidenceIds: [evidence.id],
        // Omit providers for Tier 2; service resolved by preferredService
        requestedBy: currentUser?.displayName || currentUser?.email,
        orderName: `Configured Analysis - ${selectedTemplate ? Object.values(ANALYSIS_TEMPLATES).find(t => t.id === selectedTemplate)?.name || 'Professional Analysis' : 'Professional Analysis'} - ${new Date().toLocaleDateString()}`,
        // 🔧 Include user-selected targets, tiers, and scan tier analysis request
        userSelectedTargets: selectedTargets,
        targetTiers: targetTiers, // NEW: Per-target tier selection
        analysisRequest: {
          ...pendingAnalysisRequest,
          scanTier: 'configured_scan' // Ensure scan tier is included
        }
      };

      log.debug('🤖 THE OPERATOR: Creating unified analysis order...', {
        orderConfig,
        originalAnalysisRequest: pendingAnalysisRequest
      });

      const result = await createOrder(orderConfig);

      console.log('🎉 THE OPERATOR: Analysis order created successfully:', result.data);
      
      // Close modal and notify parent component
      setShowConfirmationModal(false);
      setPendingAnalysisRequest(null);
      
      if (onAnalysisStarted) {
        // Adapt order result to expected format for basic analysis UI
        onAnalysisStarted({
          success: true,
          orderId: result.data.orderId,
          totalJobs: result.data.totalJobs,
          message: result.data.message
        });
      }

    } catch (error) {
      console.error('❌ THE OPERATOR: Error launching analysis:', error);
      notify({ severity: 'error', title: 'Launch Failed', message: error.message || 'Error launching analysis.' });
    } finally {
      setIsLaunching(false);
    }
  };

  // Cancel analysis launch
  const cancelAnalysisLaunch = () => {
    setShowConfirmationModal(false);
    setPendingAnalysisRequest(null);
  };

  return (
    <div className="analysis-config-menu">
      <div className="analysis-config-header">
        <FaRobot className="analysis-header-icon" />
        <div className="analysis-header-text">
          <h3>AI Analysis Configuration</h3>
          <p>Configure professional analysis parameters for {evidence?.fileName || 'your evidence'}</p>
        </div>
      </div>

      {/* Analysis Templates - Hidden for now */}
      {false && (
        <div className="config-section">
          <h4>Quick Start Templates</h4>
          <div className="template-scroll-container">
            <button 
              className="scroll-arrow scroll-left"
              onClick={scrollLeft}
            >
              <FaChevronLeft />
            </button>
            
            <div className="template-cards-scroll" ref={templateScrollRef}>
              {analysisTemplates.map((template, index) => (
                <div 
                  key={template.id}
                  className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                  onClick={() => applyTemplate(template)}
                  style={{
                    transform: `translateX(-${currentScrollIndex * 100}%)`,
                    transition: 'transform 0.3s ease'
                  }}
                >
                  <div className="template-header">
                    <h5>{template.name}</h5>
                    <span className="template-cost">${template.estimatedCost}</span>
                  </div>
                  <p>{template.description}</p>
                  <div className="template-targets">
                    {template.targets.map(targetId => {
                      const target = analysisTargets.find(t => t.id === targetId);
                      return target ? (
                        <span key={targetId} className="target-tag">
                          {target.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              className="scroll-arrow scroll-right"
              onClick={scrollRight}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Analysis Targets */}
      <div className="analysis-config-section">
        <h4>Analysis Targets</h4>
        
        {/* V4 Info Badge */}
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>✨</span>
          <div style={{ flex: 1 }}>
            <div style={{ 
              color: '#a78bfa', 
              fontSize: '13px', 
              fontWeight: 600,
              marginBottom: '4px'
            }}>
              V4 Target-Centric Analysis Available
            </div>
            <div style={{ 
              color: 'rgba(255, 255, 255, 0.6)', 
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              New targets: 🌦️ <strong>Environment</strong> (weather, lighting) and 📹 <strong>Videographer</strong> (camera movements, operator intent). Each target automatically includes Activities, Relationships, and Event Sequences - no separate "Activities" scan needed!
            </div>
          </div>
        </div>
        
        <div className="analysis-target-cards">
          {analysisTargets.map(target => {
            const Icon = target.icon;
            const isSelected = selectedTargets.includes(target.id);
            const currentTier = targetTiers[target.id] || 'professional';
            
            return (
              <div 
                key={target.id}
                className={`analysis-target-card ${isSelected ? 'selected' : ''} ${target.disabled ? 'disabled' : ''}`}
              >
                {/* Clickable area for target selection (everything except tier selector) */}
                <div 
                  onClick={() => !target.disabled && toggleTarget(target.id)}
                  style={{ cursor: target.disabled ? 'not-allowed' : 'pointer' }}
                >
                  <div className="analysis-card-header">
                    <Icon className="analysis-card-icon" />
                    <h5>{target.name}</h5>
                    {isSelected && <FaCheckCircle className="analysis-selected-icon" />}
                  </div>
                  <p>{target.description}</p>
                  <div className="analysis-card-cost">
                    ${target.cost}/min
                    {isSelected && (
                      <span style={{ fontSize: '10px', display: 'block', marginTop: '2px', color: 'rgba(255,255,255,0.5)' }}>
                        {currentTier === 'standard' && '~0.5-1x base'}
                        {currentTier === 'professional' && '~1-2x base'}
                        {currentTier === 'forensic' && '~2-4x base'}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Tier Selection (only shown if selected and not disabled) */}
                {isSelected && !target.disabled && (
                  <div className="analysis-tier-selector" onClick={(e) => e.stopPropagation()}>
                    <div className="tier-selector-header">
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Analysis Depth
                      </span>
                    </div>
                    <div className="tier-options">
                      {['standard', 'professional', 'forensic'].map(tier => {
                        const isForensic = tier === 'forensic';
                        const isDisabled = isForensic; // Disable forensic for now
                        
                        return (
                          <label 
                            key={tier} 
                            className={`tier-option ${isDisabled ? 'disabled' : ''}`}
                            style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          >
                            <input
                              type="radio"
                              name={`tier-${target.id}`}
                              value={tier}
                              checked={currentTier === tier}
                              onChange={() => !isDisabled && setTargetTiers(prev => ({ ...prev, [target.id]: tier }))}
                              disabled={isDisabled}
                            />
                            <span className="tier-label">
                              {tier === 'standard' && '⚡'}
                              {tier === 'professional' && '💼'}
                              {tier === 'forensic' && '🔬'}
                              {' '}
                              {tier.charAt(0).toUpperCase() + tier.slice(1)}
                            </span>
                            <span className="tier-sublabel">
                              {tier === 'standard' && 'Quick overview'}
                              {tier === 'professional' && 'Detailed analysis'}
                              {tier === 'forensic' && 'Legal-grade (Coming Soon)'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="tier-subscription-note">
                      <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
                        {currentTier === 'standard' && '✓ Available on Basic plan'}
                        {currentTier === 'professional' && '✓ Requires Pro plan or higher'}
                        {currentTier === 'forensic' && '✓ Requires Expert plan'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Model Selection */}
      <div className="analysis-config-section">
        <h4>AI Model Selection</h4>
        {/* Tier Selector */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'google-vertex-vision-standard', label: 'Standard' },
            { id: 'google-vertex-vision-flash', label: 'Flash' },
            { id: 'google-vertex-vision-pro', label: 'Pro' }
          ].map(tier => (
            <button
              key={tier.id}
              onClick={() => {
                setSelectedTier(tier.id);
                if (tier.id !== 'all') {
                  // If current model not in this tier, auto-select first model in tier
                  const modelsInTier = availableModels.filter(m => mapModelToPreferredService(m.id) === tier.id);
                  if (!modelsInTier.some(m => m.id === selectedModel) && modelsInTier.length > 0) {
                    setSelectedModel(modelsInTier[0].id);
                  }
                }
              }}
              className={`analysis-tier-chip ${selectedTier === tier.id ? 'active' : ''}`}
            >
              {tier.label}
            </button>
          ))}
        </div>
        <div className="analysis-model-selection-grid">
          {availableModels
            .filter(model => selectedTier === 'all' || mapModelToPreferredService(model.id) === selectedTier)
            .map(model => {
            const isSelected = selectedModel === model.id;
              const modelTier = mapModelToPreferredService(model.id);
            return (
              <div 
                key={model.id}
                className={`analysis-model-card ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedModel(model.id)}
              >
                <div className="analysis-model-header">
                  <FaRobot className="analysis-model-icon" />
                  <div className="analysis-model-info">
                    <h5>{model.name}</h5>
                    <p className="analysis-model-provider">{model.provider}</p>
                  </div>
                  <span className="tier-badge" style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#ffffff'
                  }}>
                    {modelTier === 'google-vertex-vision-pro' ? 'Pro' : modelTier === 'google-vertex-vision-flash' ? 'Flash' : 'Standard'}
                  </span>
                  {isSelected && <FaCheckCircle className="analysis-selected-icon" />}
                </div>
                
                <div className="analysis-model-characteristics">
                  <div className="analysis-characteristic">
                    <span className="label">Speed:</span>
                    <span className={`value speed-${model.speed}`}>{model.speed}</span>
                  </div>
                  <div className="analysis-characteristic">
                    <span className="label">Accuracy:</span>
                    <span className={`value accuracy-${model.accuracy}`}>{model.accuracy}</span>
                  </div>
                  <div className="analysis-characteristic">
                    <span className="label">Cost:</span>
                    <span className="value">${model.cost_per_1k_tokens}/1k tokens</span>
                  </div>
                </div>
                
                <div className="analysis-model-best-for">
                  <span className="analysis-best-for-label">Best for:</span>
                  <div className="analysis-best-for-tags">
                    {model.best_for.slice(0, 2).map(use => (
                      <span key={use} className="analysis-best-for-tag">{use.replace('_', ' ')}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>



      {/* Advanced Multi-Provider Configuration */}
      <div className="analysis-config-section">
        <button 
          className="analysis-advanced-modal-trigger-minimal"
          onClick={() => setShowAdvancedModal(true)}
        >
          <FaCog className="analysis-advanced-icon-red" />
        </button>
      </div>

      {/* Precision Mode Toggle */}
      <div className="analysis-config-section" style={{ marginTop: '24px', marginBottom: '16px' }}>
        <div style={{
          background: precisionMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(100, 100, 100, 0.1)',
          border: precisionMode ? '2px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(100, 100, 100, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                  🎯 Precision Analysis
                </h4>
                {precisionMode && (
                  <span style={{
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#60a5fa',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase'
                  }}>
                    ENABLED
                  </span>
                )}
              </div>
              
              <p style={{ 
                margin: '0 0 12px 0', 
                fontSize: '13px', 
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.5'
              }}>
                {precisionMode ? (
                  <>
                    <strong style={{ color: '#60a5fa' }}>Court-grade accuracy enabled.</strong> Video Intelligence API will cross-verify all detections for maximum legal defensibility.
                  </>
                ) : (
                  'Enable Video Intelligence API verification for court-grade accuracy with frame-precise timestamps and cross-validated license plates.'
                )}
              </p>

              {precisionMode && (
                <div style={{ 
                  marginTop: '12px',
                  padding: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  lineHeight: '1.6',
                  color: 'rgba(255, 255, 255, 0.8)'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '6px', color: '#60a5fa' }}>
                    ✓ Precision Features:
                  </div>
                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    <li>Frame-accurate timestamps (±0.001s precision)</li>
                    <li>Cross-verified license plates (OCR matching)</li>
                    <li>Video integrity verification (cut detection)</li>
                    <li>Chain of custody audit trail</li>
                    <li>Stationary event detection</li>
                  </ul>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <label className="precision-toggle-switch">
                <input
                  type="checkbox"
                  checked={precisionMode}
                  onChange={(e) => setPrecisionMode(e.target.checked)}
                />
                <span className="precision-toggle-slider"></span>
              </label>
              <div style={{ 
                fontSize: '12px', 
                fontWeight: 600,
                color: precisionMode ? '#60a5fa' : 'rgba(255, 255, 255, 0.5)',
                textAlign: 'center'
              }}>
                {precisionMode ? (
                  <div>
                    <div>$0.40</div>
                    <div style={{ fontSize: '10px', fontWeight: 400 }}>per video</div>
                  </div>
                ) : (
                  <div>
                    <div>$0.15</div>
                    <div style={{ fontSize: '10px', fontWeight: 400 }}>standard</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Launch Button */}
      <div className="analysis-config-footer">
        <div className="analysis-cost-breakdown">
          <div className="analysis-cost-summary">
            <span className="analysis-cost-label">Estimated Cost:</span>
            <span className="analysis-cost-value">${costEstimation.estimatedCost}/min</span>
          </div>
          {!costEstimation.valid && costEstimation.reason && (
            <div style={{
              marginTop: '6px',
              color: '#b91c1c',
              fontSize: '12px',
              fontWeight: 600
            }}>
              Cannot launch: {costEstimation.reason}
            </div>
          )}
        </div>
        
        <button 
          className="analysis-launch-btn"
          onClick={launchAnalysis}
          disabled={isLaunching || selectedTargets.length === 0 || costEstimation.valid === false}
        >
          {isLaunching ? (
            <>
              <FaRobot className="analysis-btn-icon spinning" />
              Launching Configured Analysis...
            </>
          ) : (
            <>
              <FaPlay className="analysis-btn-icon" />
              Launch Configured Analysis
            </>
          )}
        </button>
      </div>

      {/* Analysis Confirmation Modal */}
      <AnalysisConfirmationModal
        isOpen={showConfirmationModal}
        onClose={cancelAnalysisLaunch}
        onConfirm={confirmAndLaunchAnalysis}
        analysisRequest={pendingAnalysisRequest}
        evidence={evidence}
        costEstimation={costEstimation}
        currentUser={currentUser}
      />

      <AdvancedAnalysisModal
        isOpen={showAdvancedModal}
        onClose={() => setShowAdvancedModal(false)}
        evidence={evidence}
        casefileId={casefileId}
        initialTargets={selectedTargets}
        onAnalysisComplete={(orderId) => {
          console.log('Advanced analysis order created:', orderId);
          if (onAnalysisStarted) {
            onAnalysisStarted(orderId);
          }
        }}
      />
    </div>
  );
};

export default AnalysisConfigurationMenu; 